import type { CSSProperties } from 'react';
import { useImageUpload } from '../utils/use-image-upload';
import styles from '../template-styles.module.css';
import type { MediaWidgetSize, MediaWidgetProps } from './widget-types';

export type LogoWidgetSize = MediaWidgetSize;

export interface LogoWidgetProps extends MediaWidgetProps {
  blockId?: string;
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: string;
}

export default function LogoWidget({
  blockId,
  src,
  setImage,
  size = 'original',
  height = 32,
  scale = 100,
  align = 'center',
  background,
  border,
  borderStyle,
  borderColor,
  borderRadius,
  padding = '24px',
}: LogoWidgetProps) {
  const [loading, upload] = useImageUpload(setImage);

  const resolvedHeight = size === 'scale' ? Math.round(height * (scale / 100)) : height;

  const boxStyle: CSSProperties = {
    background,
    border: border ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? 'transparent'}` : undefined,
    borderRadius,
    padding,
  };

  const interactiveProps = setImage
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': src ? 'Change logo' : 'Add logo',
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            upload();
          }
        },
      }
    : undefined;

  const logoImage = (
    <div
      onClick={upload}
      data-cont={setImage ? 'img' : undefined}
      className={loading ? styles.imgLoading : undefined}
      style={size === 'fill' ? { width: '100%' } : undefined}
      {...interactiveProps}
    >
      <img
        height={size === 'fill' ? undefined : resolvedHeight}
        src={src}
        style={{
          display: 'block',
          width: size === 'fill' ? '100%' : undefined,
          minWidth: size === 'fill' ? 0 : undefined,
          margin: align === 'center' ? '0 auto' : undefined,
        }}
        alt=""
      />
    </div>
  );

  return (
    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
      <tbody>
        <tr>
          <td data-block-id={blockId} align={align} valign="middle" style={boxStyle}>
            {src ? (
              logoImage
            ) : (
              <div
                onClick={upload}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: setImage ? 'pointer' : 'default',
                  width: size === 'fill' ? '100%' : Math.round(resolvedHeight * 3),
                  height: resolvedHeight,
                  border: '1px dashed currentColor',
                  borderRadius,
                  fontSize: '11px',
                  fontFamily: 'Arial, sans-serif',
                  opacity: 0.5,
                  margin: align === 'center' ? '0 auto' : undefined,
                }}
                {...interactiveProps}
              >
                Add logo
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
